'use client';

import * as Sentry from '@sentry/nextjs';
import { useCallback, useEffect } from 'react';
import {
  type ConfirmLogin as ConfirmLoginInterface,
  type LoginForm as LoginFormInterface,
  login,
  type User,
} from '@/api/auth';
import {
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS,
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
} from '@/helpers/constants';
import { useSwrMutation } from './swr';

const ERRORS_COUNT_STORAGE_NAME = 'login';

const saveErrorsCountToStorage =
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS && AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL
    ? {
        storageName: ERRORS_COUNT_STORAGE_NAME,
        timeLeftToClear: AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
      }
    : undefined;

export const useLogin = () => {
  const {
    trigger: requestLogin,
    requestCount,
    resetRequestCount,
    data,
    error,
    isMutating,
  } = useSwrMutation<User>({
    key: 'loginKey',
    fetcher: login,
    options: { populateCache: true, saveErrorsCountToStorage },
  });

  const handleLogin = useCallback(
    async (values: LoginFormInterface | ConfirmLoginInterface) => {
      try {
        await requestLogin(values);
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setTags({
            category: 'login',
            pathToFile: 'src/hooks/login.ts',
          });

          scope.setLevel('error');

          Sentry.captureMessage(
            `Error in login request - ${JSON.stringify(error)}`
          );
        });
      }
    },
    [requestLogin]
  );

  useEffect(() => {
    if (data) {
      resetRequestCount();
    }
  }, [data, resetRequestCount]);

  return {
    handleLogin,
    userData: data,
    loginError: error,
    loginPending: isMutating,
    requestCount,
    resetRequestCount,
  };
};
